import { useCallback, useLayoutEffect, useRef, useState } from 'react';
import type { IPartnersCompany } from 'app/services/partnersApi';
import { NON_CLICKABLE_OPAQUE } from '../readOnly';
import { clearMenus, useDropdownAutoClose } from 'hooks/useBootstrapDropdown';

/**
 * Phase 7a/7b — `ul#partnersTab`, the left-hand company rail.
 *
 * ★ THE RAIL HAS TWO DIFFERENT MARKUP SHAPES, AND THAT IS THE BUG.
 * `variant="template"` is `tmpl/partners.tmpl:88-112`, rendered once by
 * `render()`. `variant="search"` is `companyRender` (`partners.js:313-355`), a
 * hand-written HTML string that was copy-pasted from the DEAD template
 * `tmpl/partners_all_company_unit.tmpl` and drifted. `searchCompanies` empties
 * `.company-fields-block` and re-appends through that second path, so the
 * FIRST company search permanently swaps the rail's shape for the rest of the
 * session — clearing the search box re-renders through `companyRender` too, so
 * only a page reload restores the template shape. `variant` is therefore
 * sticky, exactly like the legacy DOM (see `PartnersPage`'s `companyResults`).
 *
 * The four differences between the two shapes (discovery Q3/Q4/Q10):
 *
 *  | | template (`partners.tmpl:90-110`) | search (`partners.js:318-349`) |
 *  |---|---|---|
 *  | nesting | `li > div.col-xs-12 > div.row.select-row` | `li > div.row > div.col-xs-12 > div.row.select-row` (:321-323) |
 *  | name `maxlength` | `55` (:95) | `30` (:327) |
 *  | `aria-controls` | `{{name}}` (:94) | the literal `"Company Name"` (:327) |
 *  | delete item | `class="remove company_delete"` (:103) | `class="remove"` — **no `company_delete`** (:338) |
 *  | extra item | — | `<a data-id="13" class="edit">Edit</a>` (:341) — NOT ported, see Q4 |
 *
 * ★ Q3 ★★ — THE MISSING `company_delete` IS A REAL, PERMANENT REGRESSION.
 * `events` binds `"click .company_delete"` (`partners.js:30`), so once the rail
 * is search-shaped the delete menu item matches no handler and opens no popup.
 * Deleting a company is impossible for the rest of the session. The owner has
 * explicitly decided to MIRROR this, not fix it.
 * 7c honours it structurally: the delete handler is DELEGATED on
 * `#ksdm-content` against the literal `.company_delete` selector
 * (`PartnersPage`, mirroring `partners.js:30`), so the search shape's
 * `class="remove"` anchor matches nothing and opens no popup — exactly as in
 * legacy. There is no per-anchor `onClick` to accidentally "fix" it.
 * ★ Q3 IS NOT LITERALLY PERMANENT, in legacy or here: `render()` (create or
 * delete a company) rebuilds the rail from the TEMPLATE, so adding a company
 * after a search restores delete. `#add_company` lives in the header strip and
 * is untouched by a search. See `PartnersPage.doRender`.
 *
 * ★ Q4 — THE PHANTOM `Edit` ITEM, THE ONE DELIBERATE DEVIATION IN THIS FILE.
 * `companyRender` hardcodes `data-id="13"` on every row's Edit link
 * (`partners.js:341`), so `editCompanyName` looks up `input[data-id="13"]` — a
 * company id that does not exist on dev3 — hits its `if (!elem) return` guard
 * (`:439`) and does nothing at all. `.edit` is unreachable before the first
 * search (count 0 in the rendered DOM) and inert after it.
 * It WAS reproduced here under the parity-only rule. The owner has since
 * confirmed it is a legacy bug rather than a feature and asked for it to be
 * dropped, so the item is no longer emitted in EITHER shape and the kebab
 * offers Delete only. This is the one place where a live-legacy DOM diff is
 * expected: legacy's search shape has `.edit` count 42, ours has 0.
 * `unlockByDataId` is retained — the anchor/input click path and the
 * create-company layout effect both still use it.
 *
 * ★ THREE DUPLICATE-ID FAMILIES LIVE HERE (discovery §1 / Q12 — 97 duplicate
 * ids page-wide, all deliberate):
 *   1. `id="company-tab"` on all 42 `.col-xs-10.company-tab` divs;
 *   2. `id={company.id}` on the `<a>` — this is what `getActiveCompanyId`
 *      reads (`e.target.id`, `partners.js:114`);
 *   3. `id={company.id}` AGAIN on the `<input>` inside that same anchor.
 * (2) and (3) collide with each other by design: `editCompanyName` resolves the
 * input by `input[data-id="<id>"]` and relies on `getElementById`-style
 * first-match semantics elsewhere. Invalid HTML, kept verbatim — the discovery
 * calls it load-bearing and the parity-only rule applies. No eslint config is
 * checked into this repo so nothing needs suppressing today; if one lands,
 * suppress the rule here rather than de-duplicating the ids.
 *
 * ★ SERVER ORDER, NEVER SORTED. `GET /projects/companies/` is ordered by `name`
 * (`project/models.py:54 Meta.ordering`) and the Backbone view renders
 * `toJSON()` as-is. `searchCompanies` wraps the survivors in a fresh
 * `Backbone.Collection` with NO comparator (`partners.js:265`), so the filtered
 * rail keeps that same order. No client-side sort — do not add one.
 *
 * ★ TAB ACTIVATION IS BOOTSTRAP 3, NOT APP CODE (`data-toggle="tab"`).
 * `react.html` does not ship Bootstrap's JS, so `Tab.prototype.activate`
 * (bootstrap.js:2063-2100) is reproduced here exactly:
 *   - the `<li>` gets `active` (no `in`: the container has no `.fade` children
 *     at that level, so `transition` is false for the li leg);
 *   - the pane gets `active` and then `in` (it IS `.fade`) — see PartnersPage;
 *   - the anchor gets `aria-expanded="true"`, the previously-active one
 *     `aria-expanded="false"`. An anchor that has NEVER been active carries no
 *     `aria-expanded` at all, which is why `visited` is threaded in. A search
 *     re-render throws every anchor away, so `PartnersPage` resets `visited` to
 *     just the row `companyRender`'s synthetic click lands on (`:354`);
 *   - `aria-selected` is NOT touched by Bootstrap — both templates hardcode
 *     `"true"` on every anchor, and so do we;
 *   - clicking the already-active tab is a no-op (`bootstrap.js:2034`);
 *   - the data-api handler calls `preventDefault()`, so `href="#id-<n>"` never
 *     reaches the URL bar.
 *
 * ★ THE KEBAB IS HOVER-REVEALED BY CSS (discovery §6.2) AND CLICK-TOGGLED BY
 * BOOTSTRAP. `.dropdown_company` is `display:none` until `.select-row:hover`
 * (styles.css:108, :170); the app's only JS is `mouseover`/`mouseleave`
 * (`partners.js:71-88`), ported verbatim below including its bugs. The CLICK
 * leg belongs to `bootstrap.js` and lives in `../useBootstrapDropdown`.
 *
 * ★ 7c — `click .company_delete` and `change .change_name` are BOTH delegated on
 * `#ksdm-content` by `PartnersPage`, mirroring `partners.js:30` and `:39`. The
 * rename handler ends with `event.target.setAttribute("readonly","true")`
 * (`:455`), an IMPERATIVE write that this component's `readOnly` prop must not
 * fight: React only touches a DOM property when the prop VALUE changes between
 * renders, and `unlocked` still holds the id at that point, so the attribute
 * sticks until the next click re-runs `unlockByDataId` — which is exactly
 * legacy's lock/unlock cycle. Do NOT "sync" `unlocked` from the rename.
 */
interface CompanyRailProps {
  /** Already filtered when `variant === 'search'` — this component never
   * filters, exactly like `companyRender`, which is handed a collection. */
  companies: IPartnersCompany[];
  /** Which of the two markup shapes to emit. Sticky once `'search'` — Q3/Q10. */
  variant: 'template' | 'search';
  activeId: number | null;
  /** Ids that have been the active tab at least once (Bootstrap aria parity). */
  visited: ReadonlySet<number>;
  onActivate: (id: number) => void;
  /** `getActiveCompanyId` (`partners.js:112-116`) — raw `e.target.id`, garbage
   * included. See the handler below and Q6. */
  onCompanyTabClick: (targetId: string) => void;
  /**
   * 7c — `render(addNewCompany = true)` (`partners.js:105-109`): the id of the
   * company that was just created, or `null` on every other render. Drives the
   * two synthetic triggers legacy fires at the FIRST row after a create-company
   * re-render. `PartnersPage` clears it back to `null` on the following commit,
   * so a later remount (a search) cannot re-fire them.
   */
  newCompanyId?: number | null;
  /**
   * ★ NOT A LEGACY PROP. The companies this user owns and may therefore write
   * to — see `PartnersPage.editableIds` for why ownership is the rule. Two
   * effects here, both for a row NOT in this set:
   *
   *  - the name input stays `readonly` and carries `non-clickable`, so it is
   *    greyed and takes no pointer events. Selecting the tab still works — the
   *    click lands on the enclosing `<a>` and `handleAnchorClick` still calls
   *    `onActivate` — only the unlock half of `editCompanyName` is skipped, so
   *    a click on the name selects the company instead of putting a caret in it.
   *  - the ⋮ kebab is not rendered at all, so it cannot appear on hover. Its
   *    only item is Delete, and Delete is precisely what ownership forbids.
   */
  editableIds?: ReadonlySet<number>;
}

/** Nothing is editable until `PartnersPage` says so — read-only is the safe
 * direction to default in. */
const NO_EDITABLE_IDS: ReadonlySet<number> = new Set<number>();

const CompanyRail = ({
  companies,
  variant,
  activeId,
  visited,
  onActivate,
  onCompanyTabClick,
  newCompanyId = null,
  editableIds = NO_EDITABLE_IDS,
}: CompanyRailProps) => {
  const ulRef = useRef<HTMLUListElement | null>(null);
  const isSearch = variant === 'search';

  /**
   * `.open` — added by the mouseover timer to the button's PARENT, i.e. the
   * bare `<div class="">` between `.dropdown_company` and the button
   * (`partners.js:75`: `e.currentTarget.parentNode.classList.add('open')`).
   * Bootstrap's `.open > .dropdown-menu { display:block }` is what makes the
   * menu appear, so the class has to sit on that exact node — and it is the
   * same node `getParent()` hands Bootstrap's click toggle.
   */
  const [openMenuId, setOpenMenuId] = useState<number | null>(null);

  /**
   * `.top` — the flip-up variant, applied to BOTH the button and its next
   * sibling `ul.dropdown-menu` (`partners.js:79-80`). Recomputed on every
   * mouseover and never cleared anywhere else, so several rows can carry it at
   * once; a plain map mirrors that.
   */
  const [topRows, setTopRows] = useState<Record<number, boolean>>({});

  /**
   * `readonly` removal (`editCompanyName`, `partners.js:433-442`). Legacy never
   * puts it back on tab switch — only the rename `change` handler does (7c) —
   * so an unlocked row stays unlocked. A Set, not a single id.
   *
   * A search re-render DOES reset it: `companyRender` emits `readonly` on every
   * input (`partners.js:327`). That reset comes free from the remount
   * `PartnersPage` forces with a changing `key`.
   */
  const [unlocked, setUnlocked] = useState<ReadonlySet<number>>(() => new Set());

  /** `editableIds`, readable from the identity-stable callbacks below. */
  const editableIdsRef = useRef<ReadonlySet<number>>(editableIds);
  editableIdsRef.current = editableIds;

  /** `clearMenus()` closing THIS rail's menu — bootstrap.js:842. */
  const closeMenu = useCallback(() => setOpenMenuId(null), []);
  useDropdownAutoClose(openMenuId !== null, closeMenu);

  /**
   * `mouseover .btn-company-delete.icon-more-actions` — `partners.js:71-85`,
   * ported verbatim, bugs included:
   *
   *  - the 150 ms timer is NEVER cancelled. `mouseleave` removes `.open`, but a
   *    timer already in flight still adds it afterwards. Harmless in practice
   *    (`.dropdown_company` is `display:none` off-hover) — reproduced by NOT
   *    clearing it. Legacy's own `clearTimeout(timer)` runs INSIDE the callback
   *    and is a no-op.
   *  - the `pos` formula MIXES COORDINATE SYSTEMS: `el.height()` is the rail's
   *    content height, `getBoundingClientRect().top` is viewport-relative, and
   *    `el.offset().top` is document-relative. `clientHeight` equals jQuery's
   *    `.height()` here because `ul.nav` has no vertical padding or border.
   *    Kept as-is — the `< -250` threshold only makes sense against this exact
   *    (wrong) expression.
   */
  const handleKebabMouseOver = useCallback(
    (e: React.MouseEvent<HTMLButtonElement>, id: number) => {
      const el = ulRef.current;
      const btnTop = e.currentTarget.getBoundingClientRect().top;
      const ulOffsetTop = el ? el.getBoundingClientRect().top + window.pageYOffset : 0;
      const pos = (el?.clientHeight ?? 0) - btnTop - ulOffsetTop;

      window.setTimeout(() => setOpenMenuId(id), 150);

      const flip = pos < -250;
      setTopRows((prev) => (prev[id] === flip ? prev : { ...prev, [id]: flip }));
    },
    []
  );

  /**
   * `Dropdown.prototype.toggle` (bootstrap.js:763-794) — the CLICK leg the
   * partners view does not own. Was inert in wave A because `react.html` ships
   * no Bootstrap JS; restored here because it is read-only behaviour:
   *   - `clearMenus()` first (bootstrap.js:770), globally;
   *   - re-open only if this menu was NOT already open (bootstrap.js:772), so a
   *     click on a hover-opened menu shuts it and a second click re-opens it;
   *   - `return false` (bootstrap.js:793) = preventDefault + stopPropagation,
   *     which is what keeps the document-level `clearMenus` from undoing it.
   */
  const handleKebabClick = useCallback(
    (e: React.MouseEvent<HTMLButtonElement>, id: number) => {
      const isActive = openMenuId === id;
      clearMenus();
      if (!isActive) setOpenMenuId(id);
      e.preventDefault();
      e.stopPropagation();
    },
    [openMenuId]
  );

  /** `mouseleave .select-row` — `$(currentTarget).find('.open').removeClass('open')`
   * (`partners.js:86-88`). Only `.open` is cleared; `.top` survives. */
  const handleRowMouseLeave = useCallback((id: number) => {
    setOpenMenuId((prev) => (prev === id ? null : prev));
  }, []);

  /**
   * `editCompanyName` (`partners.js:433-442`). Legacy shares it between TWO
   * bindings: `"click .company-tab a"` (:41, live in both variants) and
   * `"click .edit"` (:38, reachable only in the search variant). Only the
   * first is ported — the `.edit` item itself is gone, see Q4 — and the
   * create-company layout effect calls this directly.
   *
   * It reads `event.target.dataset.id` — the ACTUAL clicked node, not the
   * delegate — then `document.querySelector('input[data-id="<that>"]')`, i.e.
   * the FIRST such input in the whole document, and bails on `if (!elem)
   * return` (:439).
   */
  const unlockByDataId = useCallback((target: HTMLElement) => {
    const dataId = target.dataset?.id;
    // ★ THE RENAME GATE. A company this user does not own never unlocks, so
    // clicking its name selects the tab and leaves the input `readonly` — see
    // `editableIds`. Read through a ref so this callback's identity stays
    // stable, which is what keeps the create-company layout effect below
    // firing exactly once.
    if (!editableIdsRef.current.has(Number(dataId))) return;
    const input = document.querySelector<HTMLInputElement>(`input[data-id="${dataId}"]`);
    if (!input) return; // :439 — the guard that makes Q4 a silent no-op
    // Imperative, exactly as legacy: the class-driven `readOnly` below follows
    // on the next render, and focus must not wait for it.
    input.removeAttribute('readonly');
    input.focus();
    const numericId = Number(dataId);
    if (Number.isFinite(numericId)) {
      setUnlocked((prev) => (prev.has(numericId) ? prev : new Set(prev).add(numericId)));
    }
  }, []);

  /**
   * The anchor click runs THREE legacy handlers off one event:
   *  1. `click .company-tab a` → `editCompanyName` (`partners.js:433`);
   *  2. `click .company-tab` → `getActiveCompanyId` (`:112`) — see
   *     `handleCompanyTabClick`, which this click bubbles into just as jQuery's
   *     delegation walks from the anchor out to the div;
   *  3. Bootstrap's tab data-api → pane switch.
   *
   * ★ Quirk in (1): it reads `event.target.dataset.id`. The anchor itself has
   * NO `data-id` (only the input inside it does), so clicking the anchor's
   * padding leaves `company_delete_id` undefined → `querySelector('input
   * [data-id="undefined"]')` → null → the `if (!elem) return` guard at :439.
   * i.e. ONLY a click that lands on the input unlocks the name.
   */
  const handleAnchorClick = useCallback(
    (e: React.MouseEvent<HTMLAnchorElement>, id: number) => {
      e.preventDefault(); // Bootstrap's data-api handler does this.
      unlockByDataId(e.target as HTMLElement);
      if (id !== activeId) onActivate(id);
    },
    [activeId, onActivate, unlockByDataId]
  );

  /**
   * `getActiveCompanyId` (`partners.js:112-116`) — `self.activeCompanyId =
   * e.target.id`, bound to `click .company-tab`.
   *
   * ★ Q6 — THIS IS THE GARBAGE-CAPTURE SITE. `e.target` is whatever was
   * actually clicked, and the delegate div carries the duplicated
   * `id="company-tab"`, so a click that lands on the div's own padding (rather
   * than on the `<a>` or the `<input>`, both of which carry the company id)
   * sets `activeCompanyId` to the literal string `"company-tab"`. Contact
   * search then filters `company_id == "company-tab"`, matches nothing, and
   * `contactRender` throws on `collection[0]` (Q7/Q11). Reproduced verbatim —
   * do NOT substitute the row's id here.
   *
   * The other half of Q6 (the value being set with no user action at all) lives
   * in `PartnersPage`: `render()` ends with a synthetic click on the first
   * anchor (`partners.js:99`), and so does `companyRender` (`:354`).
   */
  const handleCompanyTabClick = useCallback(
    (e: React.MouseEvent<HTMLDivElement>) => {
      onCompanyTabClick((e.target as HTMLElement).id);
    },
    [onCompanyTabClick]
  );

  /** `dblclick .change_name` → `highlightCompanyName` (`partners.js:443-446`).
   * On its own it does NOT clear `readonly`; the two clicks that precede a real
   * double-click already did (handleAnchorClick above). */
  const handleNameDoubleClick = useCallback((e: React.MouseEvent<HTMLInputElement>) => {
    e.currentTarget.focus();
    e.currentTarget.select();
  }, []);

  /**
   * 7c / §4.1 — `render(addNewCompany = true)`'s tail (`partners.js:105-109`):
   *
   * ```js
   * var newCompanyItem = $('#partnersTab .nav-item .select-row')[0]
   * $(newCompanyItem).find('.company-tab a input').trigger('click')
   * $(newCompanyItem).find('.change_name').trigger('dblclick')
   * ```
   *
   * Both triggers hit the SAME input (the only `input` inside
   * `.company-tab a` IS `.change_name`); they exist separately because they
   * drive two different handlers:
   *   - `trigger('click')` runs the delegated `click .company-tab a` →
   *     `editCompanyName` → `removeAttribute('readonly')` + `focus()`, and
   *     bubbles on to `click .company-tab` → `getActiveCompanyId`, which sets
   *     `activeCompanyId` to the input's (duplicated) `id`, i.e. the new
   *     company's. `PartnersPage.doRender` has already set that value, since
   *     `render()`'s own synthetic click at `:99` sets it first anyway.
   *   - `trigger('dblclick')` runs `highlightCompanyName` → `focus()` +
   *     `select()`, which is what leaves "Company Name" fully selected so the
   *     user can type straight over it. Verified live: `firstReadonly:false`,
   *     `activeElement=INPUT.change_name`, selection `0..12`.
   *
   * ★ ORDER MATTERS AND SO DOES `[0]`. The row is resolved POSITIONALLY, not by
   * id — legacy takes the first `.select-row` in `#partnersTab` — which only
   * lands on the new company because `addCompany` reassigned
   * `collection.models` to put it first (`:192-193`). Reproduced positionally
   * for the same reason.
   *
   * A LAYOUT effect: legacy runs these two lines synchronously inside `render()`,
   * so the focus and the selection are already in place before the browser
   * paints. It also has to run before `PartnersPage`'s passive effect clears
   * `newCompanyId`.
   */
  useLayoutEffect(() => {
    if (newCompanyId == null) return;
    const row = ulRef.current?.querySelector('.nav-item .select-row');
    if (!row) return;
    const clicked = row.querySelector<HTMLInputElement>('.company-tab a input');
    // `editCompanyName` reads `event.target.dataset.id` — here the input, which
    // does carry `data-id`, so the lookup succeeds and the row unlocks.
    if (clicked) unlockByDataId(clicked);
    const highlighted = row.querySelector<HTMLInputElement>('.change_name');
    if (highlighted) {
      highlighted.focus();
      highlighted.select();
    }
  }, [newCompanyId, unlockByDataId]);

  return (
    <div className="col-xs-3 company-list">
      <ul
        className="nav nav-tabs company-fields-block row"
        id="partnersTab"
        role="tablist"
        ref={ulRef}
      >
        {companies.map((company) => {
          const isActive = company.id === activeId;
          const isTop = !!topRows[company.id];
          /** Ownership — the whole row's permission state. See `editableIds`. */
          const canEdit = editableIds.has(company.id);

          /* `div.row.select-row` and everything under it is byte-identical
             between the two shapes except for the four cells called out in the
             header comment. */
          const selectRow = (
            <div className="row select-row" onMouseLeave={() => handleRowMouseLeave(company.id)}>
              {/* Duplicate id — see the header comment. The onClick is
                  `getActiveCompanyId`; a plain div is what legacy delegates to,
                  so no role/keyboard affordance is added (legacy has none and
                  parity-only applies). */}
              {/* eslint-disable-next-line jsx-a11y/no-static-element-interactions, jsx-a11y/click-events-have-key-events */}
              <div
                className="col-xs-10 company-tab"
                id="company-tab"
                onClick={handleCompanyTabClick}
              >
                <a
                  className="company-fields active"
                  id={String(company.id)}
                  data-toggle="tab"
                  href={`#id-${company.id}`}
                  role="tab"
                  /* Q10 — `companyRender` hardcodes the literal string
                     "Company Name" for every row (`partners.js:327`) instead of
                     the template's `{{name}}` (`partners.tmpl:94`). */
                  /* `?? ''` because `name` is a NULLABLE column
                     (`project/models.py:38-39`): Handlebars renders `{{name}}`
                     as the empty string, i.e. `aria-controls=""` — an attribute
                     that is PRESENT but empty. Plain `{company.name}` would make
                     React drop the attribute entirely (verified against live
                     dev3 with a null-named company). */
                  aria-controls={isSearch ? 'Company Name' : company.name ?? ''}
                  aria-selected="true"
                  aria-expanded={
                    visited.has(company.id) ? (isActive ? 'true' : 'false') : undefined
                  }
                  onClick={(e) => handleAnchorClick(e, company.id)}
                >
                  <input
                    type="text"
                    /* Q10 — 55 in the template (`partners.tmpl:95`), 30 in the
                       search string (`partners.js:327`). The column is
                       varchar(55), so a search re-render silently truncates
                       what a rename can save. Mirrored, not fixed. */
                    maxLength={isSearch ? 30 : 55}
                    /* `non-clickable` is the app-wide read-only affordance
                       (`common.css`), used with `readonly` +
                       `tabindex="-1"` exactly as the legacy
                       forms do. Killing pointer events is also what makes a
                       click on the name reach the enclosing `<a>` instead, so
                       it selects the tab rather than aiming at a dead input. */
                    className={`change_name${canEdit ? '' : ' non-clickable'}`}
                    tabIndex={canEdit ? undefined : -1}
                    style={canEdit ? undefined : NON_CLICKABLE_OPAQUE}
                    defaultValue={company.name}
                    data-id={company.id}
                    id={String(company.id)}
                    /* `!canEdit` wins outright: `unlocked` can never gain an
                       id this user cannot edit (`unlockByDataId` returns
                       first), so the second test is belt and braces against a
                       future caller. */
                    readOnly={!canEdit || !unlocked.has(company.id)}
                    onDoubleClick={canEdit ? handleNameDoubleClick : undefined}
                  />
                </a>
              </div>
              {/* ★ NOT LEGACY — THE KEBAB IS NOT RENDERED AT ALL for a company
                  this user does not own, so it cannot appear on hover. Its only
                  item is Delete, and Delete is exactly what ownership forbids;
                  revealing a menu with one dead entry is worse than no menu.
                  Removing it costs no layout: `.dropdown_company` is
                  `display:none` until `.select-row:hover` (styles.css:170), and
                  `.company-tab` keeps its `col-xs-10` either way. */}
              {canEdit && (
                <div className="dropdown dropdown_company col-xs-2">
                  <div className={openMenuId === company.id ? 'open' : ''}>
                    <button
                      className={`btn btn-company-delete dropdown-toggle icon-more-actions${
                        isTop ? ' top' : ''
                      }`}
                      type="button"
                      data-toggle="dropdown"
                      onMouseOver={(e) => handleKebabMouseOver(e, company.id)}
                      onClick={(e) => handleKebabClick(e, company.id)}
                    />
                    <ul className={`dropdown-menu${isTop ? ' top' : ''}`}>
                      <li>
                        {/* ★ Q3 — the search shape drops `company_delete`
                            (`partners.js:338`), which is the ONLY class
                            `"click .company_delete"` (`partners.js:30`) matches.
                            Company deletion is therefore dead for the rest of
                            the session, and clearing the search box does not
                            bring it back because that re-renders through
                            `companyRender` too. MIRRORED ON PURPOSE — owner's
                            decision. */}
                        {/* eslint-disable-next-line jsx-a11y/anchor-is-valid */}
                        <a
                          tabIndex={-1}
                          href="#"
                          data-id={company.id}
                          className={isSearch ? 'remove' : 'remove company_delete'}
                        >
                          <i className="icon-trash" />
                          Delete
                        </a>
                      </li>
                      {/* ★ Q4 — DELIBERATE DEVIATION FROM LEGACY.
                          `companyRender` appends a second item, `<a
                          data-id="13" class="edit"><i class="icon-trash"></i>
                          Edit</a>` (`partners.js:341`), inherited from the dead
                          template `tmpl/partners_all_company_unit.tmpl:17`. The
                          `data-id` is the hardcoded literal "13" on EVERY row,
                          so `editCompanyName` looks up `input[data-id="13"]`,
                          hits its `if (!elem) return` guard (`:439`) and does
                          nothing — a dead menu item that also reuses the trash
                          icon. It was mirrored on the parity-only rule; the
                          owner has since confirmed it is a bug on legacy too
                          and asked for it to be dropped rather than reproduced,
                          so the kebab
                          now offers Delete only in BOTH rail shapes. */}
                    </ul>
                  </div>
                </div>
              )}
            </div>
          );

          return (
            <li
              key={company.id}
              className={`nav-item col-xs-12${isActive ? ' active' : ''}`}
              data-id={`item-${company.id}`}
            >
              {isSearch ? (
                /* ★ Q10 — the search string wraps the row in an extra
                   `<div class="row">` the template does not have
                   (`partners.js:321-322`). This is VISIBLE, not cosmetic:
                   `partners.scss:88-91` puts `padding-left:30px` on
                   `.nav-tabs .nav-item > div`, so in the template shape that
                   padding lands on `.col-xs-12` (which also has Bootstrap's own
                   15px) while in the search shape it lands on a `.row` (which
                   has `margin-left:-15px`) and the inner `.col-xs-12` adds its
                   15px underneath. The whole rail shifts after a search. */
                <div className="row">
                  <div className="col-xs-12">{selectRow}</div>
                </div>
              ) : (
                <div className="col-xs-12">{selectRow}</div>
              )}
            </li>
          );
        })}
      </ul>
    </div>
  );
};

export default CompanyRail;
